import { offlineStore } from './OfflineStore';

interface DownloadStatus {
    bookId: string;
    state: 'idle' | 'downloading' | 'paused' | 'completed' | 'error';
    progress: number; // 0-100
    totalChunks: number;
    downloadedChunks: number;
    error?: string;
}

type Listener = (status: DownloadStatus) => void;

class DownloadManager {
    private queue: Map<string, DownloadStatus> = new Map();
    private controllers: Map<string, AbortController> = new Map();
    private listeners: Set<Listener> = new Set();
    private restored = false;

    constructor() {
        // Load initial state? For now, we start fresh or derived from DB.
        // Ideally we persist the "Download Job" state in IDB too so it resumes after reload.
        // For V1, we'll keep it in memory-ish, but check DB for completion.
    }

    addListener(l: Listener) { this.listeners.add(l); }
    removeListener(l: Listener) { this.listeners.delete(l); }
    private notify(s: DownloadStatus) {
        this.queue.set(s.bookId, s);
        this.listeners.forEach(l => l(s));
    }

    async getStatus(bookId: string): Promise<DownloadStatus> {
        if (this.queue.has(bookId)) return this.queue.get(bookId)!;

        // Check DB to see if it's already downloaded
        const s: DownloadStatus = {
            bookId, state: 'idle', progress: 0, totalChunks: 0, downloadedChunks: 0
        };

        // This is expensive to check repeatedly, UI should cache.
        // For now, return idle. UI triggers check if needed.
        return s;
    }

    async startDownload(bookId: string, playlistUrl: string) {
        if (this.queue.get(bookId)?.state === 'downloading') return;

        const controller = new AbortController();
        this.controllers.set(bookId, controller);

        const status: DownloadStatus = {
            bookId, state: 'downloading', progress: 0, totalChunks: 0, downloadedChunks: 0
        };
        this.notify(status);

        try {
            // 1. Fetch Playlist
            const res = await fetch(playlistUrl);
            if (!res.ok) throw new Error('Failed to fetch playlist');
            const data = await res.json();
            const tracks = data.tracks.filter((t: any) => t.url && t.url.match(/\.(mp3|m4a)$/i));

            if (!tracks || tracks.length === 0) {
                throw new Error("Download aborted: Playlist has no valid audio tracks.");
            }

            status.totalChunks = tracks.length;
            this.notify({ ...status });

            // 2. Download loop
            // Concurrency: 3
            const concurrency = 3;
            const chunks = [...tracks];
            let completed = 0;

            const processChunk = async (track: any) => {
                if (controller.signal.aborted) return;

                // Extract filename from track.key or track.url
                // track.key might be full R2 path like "library/bookId/audio/0.mp3"
                // We need just the filename like "0.mp3" for OPFS storage
                let filename: string;
                if (track.key && /\.(mp3|m4a|wav|ogg)$/i.test(track.key)) {
                    // Extract basename from track.key (handles both full paths and simple filenames)
                    filename = track.key.split('/').pop() || 'audio.mp3';
                } else {
                    // Fallback: extract from URL
                    const urlPath = track.url.split('?')[0];
                    filename = urlPath.split('/').pop() || 'audio.mp3';
                }

                // Check if already exists
                const existing = await offlineStore.getChunk(bookId, filename);
                if (existing) {
                    completed++;
                    status.downloadedChunks = completed;
                    status.progress = Math.floor((completed / status.totalChunks) * 100);
                    this.notify({ ...status });
                    return;
                }

                // Fetch
                try {
                    const r = await fetch(track.url, { signal: controller.signal });
                    if (!r.ok) throw new Error('Fetch failed');
                    const blob = await r.blob();
                    await offlineStore.saveChunk(bookId, filename, blob, 'pinned');

                    completed++;
                    status.downloadedChunks = completed;
                    status.progress = Math.floor((completed / status.totalChunks) * 100);
                    this.notify({ ...status });
                } catch (e: any) {
                    if (e.name === 'AbortError') throw e;
                    console.error(`Chunk failed ${filename}`, e);
                    // We don't fail the whole download for one chunk, but we should mark it?
                    // For now, just skip. Retry logic later.
                }
            };

            // Run with concurrency
            // Simple pool
            for (let i = 0; i < chunks.length; i += concurrency) {
                if (controller.signal.aborted) break;
                const batch = chunks.slice(i, i + concurrency);
                await Promise.all(batch.map(processChunk));
            }

            if (controller.signal.aborted) return;

            status.state = 'completed';
            status.progress = 100;
            this.notify({ ...status });

        } catch (e: any) {
            if (e.name === 'AbortError') {
                status.state = 'paused'; // Cancelled effectively pauses
                this.notify({ ...status });
            } else {
                status.state = 'error';
                status.error = e.message;
                this.notify({ ...status });
            }
        } finally {
            this.controllers.delete(bookId);
        }
    }

    pauseDownload(bookId: string) {
        const c = this.controllers.get(bookId);
        if (c) c.abort();
    }

    async removeDownload(bookId: string) {
        this.pauseDownload(bookId);
        await offlineStore.removeBook(bookId);
        const s: DownloadStatus = {
            bookId, state: 'idle', progress: 0, totalChunks: 0, downloadedChunks: 0
        };
        this.notify(s);
    }
}

export const downloadManager = new DownloadManager();
